/**
 * Form Handler
 *
 * @package DesignSchool\Forms
 */

import { modal } from './modal';
import { notification } from './notification';
import { successModal } from './success-modal';
import { apiClient } from './api-client';
import { formValidation } from './validation';
import { applyUTM, refreshAttributionFromUrl, loadUTM } from './utm';
import { applyPhoneFieldsToFormData } from './phone-utils';

declare var jQuery: any;
declare var dsFormsConfig: any;
declare var smartCaptcha: any;
declare var grecaptcha: any;

export class FormHandler {
    /** Прелоадер в модалке на время загрузки разметки формы с сервера */
    private static readonly LOADER_HTML =
        '<div class="ds-modal__loader" role="status" aria-live="polite">' +
        '<span class="ds-modal__loader-spinner" aria-hidden="true"></span>' +
        '<span class="ds-modal__loader-text">Загрузка формы…</span>' +
        '</div>';

    private $: any;
    private phoneMask: any;
    /** Ключ формы, запрос которой сейчас выполняется (защита от повторных кликов) */
    private pendingFormRequest: string | null = null;
    /** Загрузка скриптов капчи по провайдеру (один скрипт на страницу) */
    private captchaScriptPromises: Record<string, Promise<void>> = {};

    constructor() {
        this.$ = jQuery;
    }

    /**
     * Initialize form handling
     */
    public init(): void {
        this.bindFormOpeners();
        this.bindFormSubmit();
        this.bindErrorClearing();
        this.bindInlineFormResubmitUnlock();
        this.bindCheckboxLabelClick();
        this.bindModalCloseClearNotifications();

        this.$(document).on('form-loaded', (_e: Event, formId?: string) => {
            if (formId) {
                this.fillUtmParams();
                this.fillClientId(String(formId));
            }
        });

        // theme footer overwrites window.forceUTM with localStorage-based UTM; jQuery ajaxComplete then fills
        // hidden inputs from localStorage. Re-apply plugin (cookie) attribution after that stack.
        this.$(document).on('ajaxComplete', () => {
            const $m = modal.getModal();
            if (!$m.length || !$m.is(':visible')) {
                return;
            }
            setTimeout(() => {
                this.fillUtmParams();
            }, 0);
        });
        
        // Get phone mask instance
        this.phoneMask = (window as any).dsFormsPhoneMask;

        this.$(() => {
            this.$('.consult__form.ds-form').each((_: number, formEl: HTMLElement) => {
                const $form = this.$(formEl);
                this.patchMetrikaFromCookie($form);
                const cookieYm = this.getYmUidFromCookie();
                if (cookieYm) {
                    this.writeMetrikaPair($form, cookieYm);
                }
                // legacy landingFormType → CRM Typelead (ДОД / дистант / очное / …)
                this.syncConsultTypelead($form);
            });
            this.initInlineCaptcha();
        });
        
        console.log('[FormHandler] Initialized');
    }

    /**
     * Clear validation/error notifications when modal is closed (success or user close)
     */
    private bindModalCloseClearNotifications(): void {
        this.$(document).on('ds-forms-modal-closed', () => {
            notification.closeAll();
        });
    }

    /**
     * Bind error clearing handlers
     */
    private bindErrorClearing(): void {
        const clearFieldError = ($el: any) => {
            const $wrapper = $el.closest('.ds-form__field, .ds-form__checkbox, .consult__field, .field');
            $wrapper.find('.ds-form__error').text('');
            $wrapper.removeClass('ds-form__field--error ds-form__checkbox--error');
            $el.removeClass('ds-form__input--error');
            $wrapper.find('.ds-form__input, .ds-form__select, .ds-form__checkbox-input, .input').removeClass('ds-form__input--error input--error');
            $wrapper.removeClass('ds-form__field--error ds-form__checkbox--error checkbox--error');
            $wrapper.find('.form__error, .consult-field-error, .ds-field-hint').remove();
        };
        // clear on input/change: covers autocomplete and autofill (keyup/paste alone miss those)
        const textFieldSelector = '.ds-form__input, .ds-form__textarea, .ds-form input.input, .ds-form input[type="tel"]';
        this.$(document).on('input change', textFieldSelector, (e: Event) => {
            clearFieldError(this.$(e.currentTarget));
            this.$(e.currentTarget).closest('.consult__field, .field').find('.form__error, .consult-field-error').remove();
        });

        // Clear errors on checkbox change (БЭМ: ds-form__checkbox)
        this.$(document).on('change', '.ds-form__checkbox-input, .checkbox__input', (e: Event) => {
            clearFieldError(this.$(e.currentTarget));
        });

        // Clear errors on select change (БЭМ: ds-form__select)
        this.$(document).on('change', '.ds-form__select', (e: Event) => {
            clearFieldError(this.$(e.currentTarget));
        });
    }

    /**
     * Bind label click to toggle checkbox (guarantees click on text works in all forms)
     */
    private bindCheckboxLabelClick(): void {
        const labelSelectors = '.ds-form__checkbox-label, .ds-form .checkbox__label';
        this.$(document).on('click', labelSelectors, (e: Event) => {
            // Don't intercept link clicks
            if (this.$(e.target).closest('a').length) {
                return;
            }
            const $label = this.$(e.currentTarget);
            const $checkbox = $label.closest('.ds-form__checkbox, .checkbox').find('.ds-form__checkbox-input, .checkbox__input');
            if ($checkbox.length && !this.$(e.target).is('input')) {
                e.preventDefault();
                $checkbox.prop('checked', !$checkbox.prop('checked'));
                $checkbox.trigger('change');
            }
        });
    }

    /**
     * Bind click events to form openers
     */
    private bindFormOpeners(): void {
        // БЭМ: ds-form-opener (уникальный класс плагина для кнопок открытия формы)
        this.$(document).on('click', '[data-ds-form-opener]:not([href="#pay-data-form"]), .ds-form-opener:not([href="#pay-data-form"]), .mailing', (e: Event) => {
            e.preventDefault();
            // Форму открывает только плагин: не даём сработать legacy-обработчику темы (.form2_opener)
            e.stopImmediatePropagation();

            const $btn = this.$(e.currentTarget);
            const formId = $btn.data('ds-form-opener') || ($btn.hasClass('mailing') ? 'mailing' : null);
            let postId = this.resolveOpenerPostId($btn);
            
            if (!formId) {
                console.warn('[FormHandler] Form ID not found');
                return;
            }
            
            // Mailing (newsletter) — external iframe in same modal
            if (formId === 'mailing') {
                const mailingUrl = (window as any).dsFormsConfig?.mailingFormUrl || 'https://lk.dashamail.ru/forms/?f=77781';
                modal.openIframe(mailingUrl, { minHeight: 450, title: 'Подписаться на рассылку' });
                console.log('[FormHandler] Opened mailing form in modal');
                return;
            }
            
            console.log(`[FormHandler] Opening form: ${formId}`);
            
            this.loadForm(formId, postId);
        });
    }

    /**
     * post_id с кнопки или со страницы (singular), чтобы REST получил курс и преселект программы
     */
    private resolveOpenerPostId($btn: any): number | undefined {
        const raw = $btn.data('post-id');
        if (raw !== undefined && raw !== null && raw !== '') {
            const n = Number(raw);
            if (Number.isFinite(n) && n > 0) {
                return n;
            }
        }
        return this.getContextPostIdFromPage();
    }

    private getContextPostIdFromPage(): number | undefined {
        const ctx = (window as any).dsFormsConfig?.contextPostId;
        if (typeof ctx === 'number' && ctx > 0) {
            return ctx;
        }
        return undefined;
    }

    /**
     * Load form HTML via AJAX (public: used when preloaded HTML must match post_id, e.g. program forms)
     */
    public async loadForm(formId: string, postId?: number): Promise<void> {
        const effectiveRequestPostId =
            postId !== undefined && postId > 0 ? postId : this.getContextPostIdFromPage();
        const requestKey = `${formId}:${effectiveRequestPostId || 0}`;

        // Повторный клик по той же кнопке, пока запрос в полёте, не должен плодить запросы
        if (this.pendingFormRequest === requestKey) {
            return;
        }
        this.pendingFormRequest = requestKey;

        try {
            // Set form ID attribute (БЭМ: data-ds-form-id)
            modal.getModal().attr('data-ds-form-id', formId);

            if (effectiveRequestPostId) {
                modal.getModal().attr('data-post-id', String(effectiveRequestPostId));
            }

            // Модалка открывается сразу с прелоадером: разметка формы едет с сервера
            modal.clearIframeModifier();
            modal.setContent(FormHandler.LOADER_HTML);
            modal.open();

            const response = await apiClient.getForm(formId, effectiveRequestPostId);

            if (!response || !response.html) {
                throw new Error('Invalid response from server');
            }

            // Пользователь успел закрыть модалку, пока грузилась форма — не открываем её заново
            if (!modal.isOpen()) {
                console.log('[FormHandler] Modal closed while loading, aborting:', formId);
                return;
            }

            const $modalRoot = modal.getModal();
            const phoneMaskApi = this.phoneMask || (window as any).dsFormsPhoneMask;
            if ($modalRoot.length && phoneMaskApi && typeof phoneMaskApi.destroyInContainer === 'function') {
                phoneMaskApi.destroyInContainer($modalRoot);
            }

            // Set content in modal
            modal.setContent(response.html);

            // Load captcha script if needed
            if (response.captcha && response.captcha.script_url) {
                this.loadCaptchaScript(response.captcha.script_url, response.captcha.provider);
            }

            // Initialize components after form is loaded
            this.initializeFormComponents(formId);

            // Collect and fill UTM parameters
            this.fillUtmParams();

            // Fill clientID from Yandex Metrika
            this.fillClientId(formId);

            // Модалка уже открыта (с прелоадером) — на всякий случай, если её успели закрыть кодом
            modal.open();

            // Trigger form-loaded event
            this.$(document).trigger('form-loaded', [formId]);

            console.log('[FormHandler] Form loaded and opened:', formId);
            
        } catch (error: any) {
            console.error('[FormHandler] Error loading form:', error);
            modal.close();
            notification.error(
                'Ошибка загрузки формы',
                error.message || 'Не удалось загрузить форму. Попробуйте еще раз.'
            );
        } finally {
            if (this.pendingFormRequest === requestKey) {
                this.pendingFormRequest = null;
            }
        }
    }

    /**
     * Капча в формах на странице (consult): разметку выводит ds_forms_inline_form_captcha(),
     * скрипт провайдера подключается здесь — в модалках его подгружает loadForm()
     */
    private initInlineCaptcha(): void {
        this.$('.ds-form .ds-form__captcha[data-captcha-script]').each((_: number, el: HTMLElement) => {
            const $captcha = this.$(el);
            const $form = $captcha.closest('.ds-form');
            if (!this.isInlineForm($form)) {
                return;
            }
            const scriptUrl = String($captcha.attr('data-captcha-script') || '');
            const provider = String($captcha.attr('data-captcha-provider') || '');
            if (scriptUrl && provider) {
                this.loadCaptchaScript(scriptUrl, provider, $form);
            }
        });
    }

    /**
     * Load captcha script dynamically
     * @param $scope Контейнер с виджетом капчи (по умолчанию — модальное окно)
     */
    private loadCaptchaScript(scriptUrl: string, provider: string, $scope?: any): void {
        this.ensureCaptchaScript(scriptUrl, provider)
            .then(() => {
                this.initializeCaptcha(provider, $scope);
            })
            .catch(() => {
                console.error(`[FormHandler] Failed to load captcha script: ${scriptUrl}`);
            });
    }

    /**
     * Подключить скрипт капчи один раз; повторные вызовы ждут ту же загрузку
     */
    private ensureCaptchaScript(scriptUrl: string, provider: string): Promise<void> {
        const globalName = provider === 'yandex' ? 'smartCaptcha' : 'grecaptcha';
        if ((window as any)[globalName]) {
            return Promise.resolve();
        }
        if (provider in this.captchaScriptPromises) {
            return this.captchaScriptPromises[provider];
        }

        const promise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.id = `ds-forms-captcha-${provider}`;
            script.async = true;
            script.defer = true;

            if (provider === 'yandex') {
                // render=onload: виджеты рисуем сами (renderSmartCaptcha), чтобы знать их id
                const onloadName = 'dsFormsSmartCaptchaOnload';
                (window as any)[onloadName] = () => resolve();
                script.src = scriptUrl + (scriptUrl.indexOf('?') === -1 ? '?' : '&') + `render=onload&onload=${onloadName}`;
            } else {
                script.src = scriptUrl;
                script.onload = () => resolve();
            }

            script.onerror = () => {
                delete this.captchaScriptPromises[provider];
                script.remove();
                reject(new Error('captcha script load failed'));
            };

            document.head.appendChild(script);
        });

        this.captchaScriptPromises[provider] = promise;
        return promise;
    }

    /**
     * Initialize captcha widget
     * @param $scope Контейнер с виджетом капчи (по умолчанию — модальное окно)
     */
    private initializeCaptcha(provider: string, $scope?: any): void {
        const $root = ($scope && $scope.length) ? $scope : modal.getModal();

        if (provider === 'yandex') {
            $root.find('.smart-captcha').each((_: number, el: HTMLElement) => {
                this.renderSmartCaptcha(el);
            });
            return;
        }

        if (provider === 'google') {
            this.updateRecaptchaToken();
        }
    }

    /**
     * Отрисовать виджет Yandex SmartCaptcha в контейнере (один раз)
     */
    private renderSmartCaptcha(el: HTMLElement): void {
        const sitekey = el.getAttribute('data-sitekey');
        if (!sitekey || typeof smartCaptcha === 'undefined' || typeof smartCaptcha.render !== 'function') {
            return;
        }
        // уже отрисован нами или автоматически (скрипт подключён без render=onload другим кодом)
        if (el.hasAttribute('data-widget-id') || el.childElementCount > 0) {
            return;
        }

        try {
            const widgetId = smartCaptcha.render(el, {
                sitekey: sitekey,
                callback: (token: string) => {
                    el.setAttribute('data-smart-token', token || '');
                }
            });
            el.setAttribute('data-widget-id', String(widgetId));
        } catch (e) {
            console.error('[FormHandler] Error initializing SmartCaptcha:', e);
        }
    }

    /**
     * Сбросить виджет капчи после отправки: токен одноразовый
     */
    private resetCaptcha($form: any): void {
        if (!$form || !$form.length) {
            return;
        }
        $form.find('.smart-captcha').each((_: number, el: HTMLElement) => {
            el.removeAttribute('data-smart-token');
            const widgetId = el.getAttribute('data-widget-id');
            try {
                if (typeof smartCaptcha !== 'undefined' && typeof smartCaptcha.reset === 'function') {
                    if (widgetId !== null) {
                        smartCaptcha.reset(Number(widgetId));
                    } else {
                        smartCaptcha.reset();
                    }
                }
            } catch (e) {
                console.warn('[FormHandler] Error resetting SmartCaptcha:', e);
            }
        });
    }

    /**
     * reCAPTCHA v3: токен живёт 2 минуты, поэтому получаем свежий прямо перед отправкой
     */
    private refreshRecaptchaToken($form: any): Promise<void> {
        const input = $form.find('input[type="hidden"][name="g-recaptcha-response"]')[0] as HTMLInputElement | undefined;
        const siteKey = (typeof dsFormsConfig !== 'undefined' && dsFormsConfig?.recaptchaSiteKey) || '';
        const api = (window as any).grecaptcha;
        if (!input || !siteKey || !api || typeof api.execute !== 'function') {
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            const timer = window.setTimeout(resolve, 3000);
            const done = () => {
                window.clearTimeout(timer);
                resolve();
            };
            try {
                api.ready(() => {
                    api.execute(siteKey, { action: 'contact' }).then((token: string) => {
                        input.value = token;
                        done();
                    }, done);
                });
            } catch (e) {
                console.warn('[FormHandler] Error refreshing reCAPTCHA token:', e);
                done();
            }
        });
    }

    /**
     * Initialize form components after loading
     */
    private initializeFormComponents(formId: string): void {
        const $modal = modal.getModal();

        if (this.phoneMask && typeof this.phoneMask.applyMasksInScope === 'function') {
            this.phoneMask.applyMasksInScope($modal);
        }

        // Update reCAPTCHA token if field exists (for Google reCAPTCHA v3)
        this.updateRecaptchaToken();

        // Dynamic selects are handled by dynamicSelects module
        // They will be initialized automatically via form-loaded event
    }

    /**
     * Update reCAPTCHA token for v3
     */
    private updateRecaptchaToken(): void {
        const recaptchaResponse = document.getElementById('recaptchaResponse');
        if (!recaptchaResponse || typeof (window as any).grecaptcha === 'undefined') {
            return;
        }

        const siteKey = dsFormsConfig?.recaptchaSiteKey || '';
        if (!siteKey) {
            return;
        }

        (window as any).grecaptcha.ready(() => {
            (window as any).grecaptcha.execute(siteKey, { action: 'contact' }).then((token: string) => {
                (recaptchaResponse as HTMLInputElement).value = token;
            });
        });
    }

    /**
     * Fill UTM from plugin cookie (utm_params). Do not use window.forceUTM: theme footer replaces it
     * with a localStorage-based implementation, so "correct cookie + stale UTM in form" was the bug.
     */
    private fillUtmParams(): void {
        const $modal = modal.getModal();
        const $scope = $modal.length ? $modal[0] : document;
        refreshAttributionFromUrl();
        applyUTM($scope);
    }

    /**
     * Yandex Metrika visitor id (cookie _ym_uid, same as getClientID)
     */
    private getYmUidFromCookie(): string {
        const m = document.cookie.match(/(?:^|;\s*)_ym_uid=([^;]*)/);
        if (!m) {
            return '';
        }
        try {
            return decodeURIComponent(m[1].trim());
        } catch {
            return m[1].trim();
        }
    }

    /**
     * Один id посетителя для clientID и _ym_uid (не UTM), чтобы дубли на бэкенде совпадали
     */
    private syncMetrikaVisitorFields(formData: FormData): void {
        const cookieYm = this.getYmUidFromCookie();
        let cid = formData.get('clientID');
        let ymUid = formData.get('_ym_uid');
        if (cookieYm && (!ymUid || ymUid === '')) {
            formData.set('_ym_uid', cookieYm);
            ymUid = cookieYm;
        }
        if (cid && (!ymUid || ymUid === '')) {
            formData.set('_ym_uid', String(cid));
            ymUid = String(cid);
        }
        if (ymUid && (!cid || cid === '')) {
            formData.set('clientID', String(ymUid));
        }
    }

    /**
     * Записать clientID и _ym_uid в поля формы (jQuery root)
     */
    private writeMetrikaPair($root: any, id: string): void {
        if (!id) {
            return;
        }
        $root.find('input[name="clientID"]').val(id);
        $root.find('input[name="_ym_uid"]').val(id);
    }

    /**
     * Подставить id из cookie _ym_uid, если поля пустые
     */
    private patchMetrikaFromCookie($root: any): void {
        const cookieYm = this.getYmUidFromCookie();
        if (!cookieYm) {
            return;
        }
        const $c = $root.find('input[name="clientID"]');
        const $y = $root.find('input[name="_ym_uid"]');
        const cv = ($c.val() as string || '').trim();
        const yv = ($y.val() as string || '').trim();
        if (!cv) {
            $c.val(cookieYm);
        }
        if (!yv) {
            $y.val(cookieYm);
        }
    }

    /**
     * Один запрос client id к Метрике (yaCounter или ym), с таймаутом
     */
    private fetchMetrikaClientIdOnce(counterId: string): Promise<string> {
        return new Promise((resolve) => {
            let finished = false;
            const done = (v: string) => {
                if (finished) {
                    return;
                }
                finished = true;
                resolve((v || '').trim());
            };
            const timer = window.setTimeout(() => done(''), 250);
            const yaCounter = (window as any)[`yaCounter${counterId}`];
            if (typeof yaCounter?.getClientID === 'function') {
                try {
                    yaCounter.getClientID((id: string) => {
                        window.clearTimeout(timer);
                        done(id || '');
                    });
                    return;
                } catch (e) {
                    console.warn('[FormHandler] yaCounter.getClientID failed:', e);
                }
            }
            const ymFn = (window as any).ym;
            if (typeof ymFn === 'function') {
                try {
                    ymFn(counterId, 'getClientID', (id: string) => {
                        window.clearTimeout(timer);
                        done(id || '');
                    });
                    return;
                } catch (e) {
                    console.warn('[FormHandler] ym getClientID failed:', e);
                }
            }
            window.clearTimeout(timer);
            done('');
        });
    }

    /**
     * Перед FormData: cookie + несколько попыток getClientID (Метрика часто грузится позже формы)
     */
    private async ensureMetrikaVisitorIds($form: any): Promise<void> {
        this.patchMetrikaFromCookie($form);
        const counterId = String((typeof dsFormsConfig !== 'undefined' && dsFormsConfig?.yandexMetrikaId) ? dsFormsConfig.yandexMetrikaId : '').trim();
        if (!counterId) {
            return;
        }
        const hasAny = (): boolean => {
            const c = ($form.find('input[name="clientID"]').val() as string || '').trim();
            const y = ($form.find('input[name="_ym_uid"]').val() as string || '').trim();
            return !!(c || y);
        };
        if (hasAny()) {
            const c = ($form.find('input[name="clientID"]').val() as string || '').trim();
            const y = ($form.find('input[name="_ym_uid"]').val() as string || '').trim();
            const merged = c || y;
            if (merged) {
                this.writeMetrikaPair($form, merged);
            }
            return;
        }
        const id = await this.fetchMetrikaClientIdOnce(counterId);
        if (id) {
            this.writeMetrikaPair($form, id);
        }
    }

    /**
     * Fill clientID and _ym_uid from Yandex Metrika / cookie
     */
    private fillClientId(formId: string): void {
        const $modal = modal.getModal();
        if (!$modal.length) {
            return;
        }
        this.patchMetrikaFromCookie($modal);

        const clientIdField = `clientID-${formId}`;
        const $field = $modal.find(`#${clientIdField}, input[name="clientID"]`);
        const metrikaId = String(dsFormsConfig?.yandexMetrikaId || '').trim();
        if (!$field.length || !metrikaId) {
            return;
        }

        const tryApis = (): void => {
            void this.fetchMetrikaClientIdOnce(metrikaId).then((id) => {
                if (id) {
                    this.writeMetrikaPair($modal, id);
                }
            });
        };
        tryApis();
        window.setTimeout(tryApis, 500);
        window.setTimeout(tryApis, 1500);
    }

    /**
     * Разблокировать кнопку инлайн-формы после успешной отправки, когда пользователь снова редактирует поля
     */
    private bindInlineFormResubmitUnlock(): void {
        const unlock = ($form: any) => {
            if (!$form.length || !$form.data('submitted')) {
                return;
            }
            $form.removeData('submitted');
            const $submitBtn = $form.find('.ds-form__submit-btn, input[type="submit"], button[type="submit"]');
            $submitBtn.data('sending', 0);
            $submitBtn.prop('disabled', false);
            $submitBtn.removeClass('ds-form__submit-btn--sent');
            const savedLabel = ($submitBtn.data('label') as string) || '';
            if (savedLabel) {
                $submitBtn.text(savedLabel);
            }
        };
        const selector = '.ds-form.consult__form input, .ds-form.consult__form textarea, .ds-form.consult__form select, .consult__form.ds-form input, .consult__form.ds-form textarea, .consult__form.ds-form select';
        this.$(document).on('input change', selector, (e: Event) => {
            unlock(this.$(e.currentTarget).closest('.ds-form'));
        });
    }

    /**
     * Форма на странице (consult), не в модальном окне
     */
    private isInlineForm($form: any): boolean {
        if (!$form || !$form.length) {
            return false;
        }
        return $form.closest('.ds-modal__body').length === 0;
    }

    /**
     * Подставить CRM Typelead для consult из window.landingFormType (как в legacy)
     */
    private syncConsultTypelead($form: any): void {
        if (!$form || !$form.length) {
            return;
        }
        const landingType = String((window as any).landingFormType || '').trim();
        if (!landingType || landingType === 'form_consult') {
            return;
        }
        let $typelead = $form.find('input[name="typelead"]');
        if (!$typelead.length) {
            $form.prepend('<input type="hidden" name="typelead" value="">');
            $typelead = $form.find('input[name="typelead"]');
        }
        $typelead.val(landingType);
    }

    /**
     * Сброс инлайн-формы после успешной отправки
     */
    private resetInlineForm($form: any): void {
        if (!$form || !$form.length) {
            return;
        }
        formValidation.clearHighlights($form);
        $form.find('.error, .consult-field-error, .form__error').remove();

        const phoneMaskApi = this.phoneMask || (window as any).dsFormsPhoneMask;
        if (phoneMaskApi && typeof phoneMaskApi.destroyInContainer === 'function') {
            phoneMaskApi.destroyInContainer($form);
        }

        const formEl = $form[0] as HTMLFormElement | undefined;
        if (formEl && typeof formEl.reset === 'function') {
            formEl.reset();
        }

        const $ts = $form.find('input[name="_ts"]');
        if ($ts.length) {
            $ts.val(String(Math.floor(Date.now() / 1000)));
        }

        if (phoneMaskApi && typeof phoneMaskApi.applyMasksInScope === 'function') {
            phoneMaskApi.applyMasksInScope($form);
        }
    }

    /**
     * Bind form submit events
     */
    private bindFormSubmit(): void {
        // БЭМ: ds-form (новая форма)
        this.$(document).on('submit', '.ds-form', (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            // block legacy theme submit handlers that can redirect current page
            if (typeof (e as any).stopImmediatePropagation === 'function') {
                (e as any).stopImmediatePropagation();
            }
            
            const $form = this.$(e.currentTarget);
            const formId = $form.data('form-id') || modal.getModal().data('ds-form-id');
            
            console.log(`[FormHandler] Submitting form: ${formId}`);
            
            this.submitForm($form, formId);
        });
    }

    /**
     * Submit form
     */
    private setFormLoading($form: any, $submitBtn: any, loading: boolean): void {
        const btnLabel = ($submitBtn.data('label') as string) || $submitBtn.text().trim();
        if (loading) {
            if (!$submitBtn.data('label')) {
                $submitBtn.data('label', btnLabel);
            }
            $submitBtn.data('sending', 1);
            $submitBtn.prop('disabled', true);
            $submitBtn.attr('aria-busy', 'true');
            $submitBtn.addClass('ds-form__submit-btn--loading');
            $form.addClass('ds-form--submitting');
            if ($submitBtn.hasClass('consult__btn')) {
                $submitBtn.text('Отправка...');
            }
            return;
        }
        $submitBtn.data('sending', 0);
        $submitBtn.prop('disabled', false);
        $submitBtn.attr('aria-busy', 'false');
        $submitBtn.removeClass('ds-form__submit-btn--loading');
        $form.removeClass('ds-form--submitting');
        const savedLabel = ($submitBtn.data('label') as string) || '';
        if (savedLabel) {
            $submitBtn.text(savedLabel);
        }
    }

    private async submitForm($form: any, formId: string): Promise<void> {
        const $submitBtn = $form.find('.ds-form__submit-btn, input[type="submit"], button[type="submit"]');

        if ($submitBtn.data('sending') === 1 || $submitBtn.hasClass('ds-form__submit-btn--loading')) {
            return;
        }

        this.setFormLoading($form, $submitBtn, true);

        const inlineForm = this.isInlineForm($form);
        let submitSucceeded = false;
        let requestSent = false;

        try {
            const validation = await formValidation.validateForm($form);
            if (!validation.valid) {
                this.setFormLoading($form, $submitBtn, false);
                const errorNames = Object.keys(validation.errors);
                $form.find('input, select, textarea').each((_: number, el: HTMLElement) => {
                    const name = this.$(el).attr('name');
                    if (name && errorNames.indexOf(name) !== -1) {
                        this.$(el).focus();
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return false;
                    }
                });
                const fieldLabels = formValidation.getFieldLabels($form, errorNames);
                const fieldsText = fieldLabels.length > 0 ? ' Поля с ошибками: ' + fieldLabels.join(', ') + '.' : '';
                notification.warning(
                    'Ошибка валидации',
                    'Пожалуйста, заполните все обязательные поля корректно.' + fieldsText
                );
                return;
            }

            this.patchMetrikaFromCookie($form);
            void this.ensureMetrikaVisitorIds($form);

            await this.refreshRecaptchaToken($form);

            // Collect form data
            const formData = new FormData($form[0]);
            applyPhoneFieldsToFormData(this.$, $form, formData);

            const phoneMaskApi = this.phoneMask || (window as any).dsFormsPhoneMask;
            if (phoneMaskApi && typeof phoneMaskApi.applyPhonesToFormData === 'function') {
                phoneMaskApi.applyPhonesToFormData(formData, $form);
            }

            // Append human-readable text for each select (option text, not value) for CRM
            $form.find('select[name]').each((_: number, selectEl: HTMLElement) => {
                const select = selectEl as HTMLSelectElement;
                const name = select.getAttribute('name');
                if (!name) return;
                const option = select.options[select.selectedIndex];
                if (option && option.value) {
                    const label = option.textContent?.trim() ?? '';
                    if (label) {
                        formData.append(name + '_label', label);
                    }
                }
            });

            // Get captcha tokens (pass $form for inline forms)
            const captchaTokens = this.getCaptchaTokens($form);
            if (captchaTokens.smartToken) {
                formData.set('smart-token', captchaTokens.smartToken);
            }
            if (captchaTokens.recaptchaToken) {
                formData.append('yacg_token', captchaTokens.recaptchaToken);
            }

            // Виджет SmartCaptcha есть в форме, но проверка не пройдена — не отправляем заявку впустую
            if ($form.find('.smart-captcha[data-widget-id]').length && !formData.get('smart-token')) {
                this.setFormLoading($form, $submitBtn, false);
                notification.warning(
                    'Подтвердите, что вы не робот',
                    'Пожалуйста, пройдите проверку капчи перед отправкой формы.'
                );
                return;
            }

            // Add meta data
            formData.append('url', window.location.href);

            refreshAttributionFromUrl();

            // referrer must be full current page URL for CRM
            formData.set('referrer', window.location.href);

            // normalize tracking fields from plugin cookie storage
            // this prevents stale values from other scripts (e.g. legacy localStorage fillers)
            const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ysclid', 'yclid'];
            const utmData = loadUTM();
            utmKeys.forEach((key: string) => {
                const cookieValue = typeof utmData[key] === 'string' ? utmData[key] : '';
                formData.set(key, cookieValue);
            });

            this.syncMetrikaVisitorFields(formData);

            // Add post_id if available (from modal data, URL param, or form data)
            const $modal = modal.getModal();
            const postIdFromModal = $modal.attr('data-post-id');
            if (postIdFromModal) {
                formData.set('post_id', postIdFromModal);
                console.log('[FormHandler] Using post_id from modal:', postIdFromModal);
            } else {
                // Try URL param
                const urlParams = new URLSearchParams(window.location.search);
                const postIdFromUrl = urlParams.get('post_id');
                if (postIdFromUrl) {
                    formData.set('post_id', postIdFromUrl);
                    console.log('[FormHandler] Using post_id from URL param:', postIdFromUrl);
                } else {
                    // Try to get from form hidden field
                    const postIdField = $form.find('input[name="post_id"], input[data-post-id]');
                    if (postIdField.length) {
                        const postId = postIdField.val() || postIdField.data('post-id');
                        if (postId) {
                            formData.set('post_id', String(postId));
                            console.log('[FormHandler] Using post_id from form field:', postId);
                        }
                    } else {
                        // Will be resolved server-side via url_to_postid from URL
                        console.log('[FormHandler] Will try to resolve post_id from URL server-side');
                    }
                }
            }

            // Submit via API
            requestSent = true;
            const response = await apiClient.submitForm(formId, formData);

            submitSucceeded = true;
            // Success (pass $form so inline form can get userName from it)
            this.handleSubmitSuccess(formId, response, $form);

        } catch (error: any) {
            // Error
            this.handleSubmitError(error, $form);
        } finally {
            if (requestSent) {
                this.resetCaptcha($form);
            }
            if (submitSucceeded && inlineForm) {
                $form.removeClass('ds-form--submitting');
                $form.data('submitted', 1);
                $submitBtn.data('sending', 1);
                $submitBtn.prop('disabled', true);
                $submitBtn.attr('aria-busy', 'false');
                $submitBtn.removeClass('ds-form__submit-btn--loading');
                $submitBtn.addClass('ds-form__submit-btn--sent');
                $submitBtn.text('Заявка отправлена');
            } else {
                this.setFormLoading($form, $submitBtn, false);
            }
        }
    }

    private openPaymentInNewTab(url: string): void {
        if (!url) return;
        const w = window.open(url, '_blank');
        if (w) {
            w.opener = null;
        }
    }

    private escapeHtmlAttr(value: string): string {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    private bindWebinarPaymentPopupButtons(): void {
        setTimeout(() => {
            document.querySelectorAll('.ds-payment-popup__btn[data-payment-link]').forEach((btn) => {
                btn.addEventListener('click', (event: Event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const link = btn.getAttribute('data-payment-link');
                    if (link) {
                        this.openPaymentInNewTab(link);
                    }
                });
            });
        }, 100);
    }

    private showWebinarPaymentPopup(
        dataTinkoffLink: string,
        sberbankUrl: string,
        formId: string,
        response: any
    ): void {
        const message = 'Вы можете оплатить вебинар сейчас или подождать, пока с вами свяжется наш менеджер.';
        let buttonsHtml = '';

        if (dataTinkoffLink) {
            buttonsHtml += '<button type="button" class="ds-payment-popup__btn" data-payment-link="'
                + this.escapeHtmlAttr(dataTinkoffLink)
                + '" data-payment-provider="tinkoff">Оплатить через Т-Банк</button>';
        }
        if (sberbankUrl) {
            buttonsHtml += '<button type="button" class="ds-payment-popup__btn" data-payment-link="'
                + this.escapeHtmlAttr(sberbankUrl)
                + '" data-payment-provider="sber">Оплатить через Сбер</button>';
        }

        const html = '<div class="ds-payment-popup">'
            + '<p class="ds-payment-popup__text">' + message + '</p>'
            + (buttonsHtml ? '<div class="ds-payment-popup__actions">' + buttonsHtml + '</div>' : '')
            + '</div>';

        (window as any).Swal.fire({
            title: 'Спасибо за вашу заявку!',
            html,
            type: 'success',
            showCancelButton: false,
            showCloseButton: true,
            showConfirmButton: !dataTinkoffLink && !sberbankUrl,
            confirmButtonColor: '#1f936d',
            confirmButtonText: 'Закрыть',
        });

        if (dataTinkoffLink || sberbankUrl) {
            this.bindWebinarPaymentPopupButtons();
        }

        this.trackMetrikaGoals(formId);
        this.$(document).trigger('form_submit_success', {
            formId: formId,
            response: response
        });
    }

    /**
     * Handle successful form submission
     * @param $form Optional form element (for inline forms, to get userName etc.)
     */
    private handleSubmitSuccess(formId: string, response: any, $form?: any): void {
        // For paid webinar form, show payment links like in theme (footer.php:344-368)
        // free_registration: сервер оформил заявку без оплаты (бесплатный вебинар) - обычное окно успеха
        if (formId === 'webinar' && !response.free_registration) {
            const dataPay = response.dataPay || {};
            const dataTinkoffLink = response.tinkoffPayUrl || response.payment_url_tinkoff || '';
            const sberbankUrl = response.payment_url_sberbank || dataPay.formUrl || '';
            
            // Close modal first before showing Swal
            modal.close();
            console.log('[FormHandler] Modal closed before payment Swal');
            
            // Check if Swal is available (from theme)
            if (typeof (window as any).Swal !== 'undefined') {
                this.showWebinarPaymentPopup(dataTinkoffLink, sberbankUrl, formId, response);
                return;
            }
            
            // Fallback: open payment URL if Swal unavailable
            if (response.redirect) {
                console.log('[FormHandler] Opening payment in new tab:', response.redirect);
                this.openPaymentInNewTab(response.redirect);
                return;
            }
        }

        // Get user name if available (from form when inline, else from modal)
        const $nameSource = ($form && $form.length) ? $form : modal.getModal();
        const $nameField = $nameSource.find('input[name="form2_name"], input[name="name"]');
        const userName = $nameField.length ? ($nameField.first().val() as string) : '';

        // Убрать отображение ошибок темы у инлайн-формы (consult: «Неверно заполнено поле»)
        if ($form && $form.length) {
            $form.find('.consult__field input, .consult__field .input, .field input').each((_: number, el: HTMLElement) => {
                this.$(el).next('span, div').text('');
            });
            $form.find('.consult__field .error, .consult__field [class*="error"], .field .error, .field [class*="error"]').text('');
            if (this.isInlineForm($form)) {
                this.resetInlineForm($form);
            }
        }

        // Close modal immediately on success
        modal.close();
        console.log('[FormHandler] Modal closed after successful submission');

        // Show technical details only in dev mode
        const isDevMode = (window as any).dsFormsConfig?.devMode === true;
        
        // Always show success popup - even if has_errors, it's still a successful submission
        // Technical details about errors are only shown in dev mode
        if (isDevMode && response.has_errors) {
            // In dev mode, log technical details to console
            console.warn('[FormHandler] Form submitted with partial errors (dev mode):', {
                has_errors: response.has_errors,
                message: response.message,
                errors: response.errors || {}
            });
        }

        // Show success modal (always successful from user perspective)
        successModal.show({
            userName: userName,
            message: 'Ваша заявка принята!',
            callback: () => {
                // Track Metrika goals after user closes the modal
                this.trackMetrikaGoals(formId);
            }
        });

        // Track Metrika goals
        this.trackMetrikaGoals(formId);

        // Trigger success event
        this.$(document).trigger('form_submit_success', {
            formId: formId,
            response: response
        });

        console.log('[FormHandler] Form submitted successfully:', formId, response);
    }

    private isTechnicalErrorMessage(message: string): boolean {
        if (!message || typeof message !== 'string') {
            return false;
        }
        return /\$\s*is not a function|is not defined|cannot read propert|undefined is not|unexpected token/i.test(
            message
        );
    }

    /**
     * Handle form submission error
     */
    private handleSubmitError(error: any, $form: any): void {
        console.error('[FormHandler] Form submission error:', error);

        let errorTitle = 'Ошибка отправки';
        let errorMessage = 'Произошла ошибка при отправке формы. Попробуйте еще раз.';
        let showFieldErrors = false;

        // Map error codes to user-friendly messages
        const errorMessages: Record<string, { title: string; message: string }> = {
            'duplicate': {
                title: 'Заявка уже отправлена',
                message: 'Вы уже отправляли заявку недавно. Пожалуйста, подождите немного перед повторной отправкой.'
            },
            'too_many_requests': {
                title: 'Слишком много запросов',
                message: 'Вы отправили слишком много заявок за короткое время. Пожалуйста, подождите минуту и попробуйте снова.'
            },
            'bot_detected': {
                title: 'Обнаружена подозрительная активность',
                message: 'Слишком много попыток отправки за короткое время. Подождите несколько минут и попробуйте отправить заявку снова. Изменение данных не поможет — ограничение временное.'
            },
            'spam_detected': {
                title: 'Заявка отклонена',
                message: 'Ваша заявка была отклонена системой защиты от спама. Если вы не робот, пожалуйста, попробуйте еще раз.'
            },
            'validation_failed': {
                title: 'Ошибка валидации',
                message: 'Пожалуйста, проверьте правильность заполнения всех полей формы.'
            },
            'payment_order_failed': {
                title: 'Ошибка оплаты',
                message: 'Не удалось создать заказ на оплату. Попробуйте еще раз или обновите страницу.'
            },
            'invalid_nonce': {
                title: 'Ошибка безопасности',
                message: 'Произошла ошибка безопасности. Пожалуйста, обновите страницу и попробуйте снова.'
            },
            'form_not_found': {
                title: 'Форма не найдена',
                message: 'Запрошенная форма не найдена. Пожалуйста, обновите страницу.'
            },
            'form_not_available': {
                title: 'Форма недоступна',
                message: 'Эта форма временно недоступна. Пожалуйста, попробуйте позже.'
            },
            'internal_error': {
                title: 'Внутренняя ошибка',
                message: 'Произошла внутренняя ошибка сервера. Пожалуйста, попробуйте еще раз через несколько минут.'
            }
        };

        // Check error code first
        if (error.code && errorMessages[error.code]) {
            const errorInfo = errorMessages[error.code];
            errorTitle = errorInfo.title;
            errorMessage = errorInfo.message;

            // For validation_failed, show field names in toast if server sent errors
            if (error.code === 'validation_failed' && error.errors && typeof error.errors === 'object') {
                const fieldNames = Object.keys(error.errors);
                if (fieldNames.length > 0 && $form && $form.length) {
                    const fieldLabels = formValidation.getFieldLabels($form, fieldNames);
                    if (fieldLabels.length > 0) {
                        errorMessage = 'Пожалуйста, исправьте ошибки в полях: ' + fieldLabels.join(', ') + '.';
                    }
                }
                showFieldErrors = true;
                fieldNames.forEach((fieldName: string) => {
                    const fieldError = error.errors[fieldName];
                    const errorText = Array.isArray(fieldError) ? fieldError[0] : fieldError;
                    formValidation.showError(fieldName, errorText, $form);
                });
            }

            // Use server message if available and more specific (only if not validation with fields)
            if (
                error.message &&
                error.message !== errorInfo.message &&
                !showFieldErrors &&
                !this.isTechnicalErrorMessage(error.message)
            ) {
                errorMessage = error.message;
            }
        }
        // Check if it's a validation error with field errors
        else if (error.errors && typeof error.errors === 'object') {
            showFieldErrors = true;
            errorTitle = 'Ошибка валидации';
            const fieldNames = Object.keys(error.errors);
            const fieldLabels = ($form && $form.length) ? formValidation.getFieldLabels($form, fieldNames) : [];
            errorMessage = fieldLabels.length > 0
                ? 'Пожалуйста, исправьте ошибки в полях: ' + fieldLabels.join(', ') + '.'
                : 'Пожалуйста, исправьте ошибки в форме.';

            // Show and highlight field errors
            fieldNames.forEach((fieldName: string) => {
                const fieldError = error.errors[fieldName];
                const errorText = Array.isArray(fieldError) ? fieldError[0] : fieldError;
                formValidation.showError(fieldName, errorText, $form);
            });
        }
        // Use error message if available (не показываем технические JS-ошибки)
        else if (error.message && !this.isTechnicalErrorMessage(error.message)) {
            errorMessage = error.message;
        }

        // Всегда показываем уведомление об ошибке
        console.log('[FormHandler] Showing error notification:', {
            title: errorTitle,
            message: errorMessage,
            code: error.code
        });
        
        try {
            notification.error(
                errorTitle,
                errorMessage
            );
            console.log('[FormHandler] Notification shown successfully');
        } catch (e) {
            console.error('[FormHandler] Failed to show notification:', e);
            // Fallback: show alert if notification fails
            alert(errorTitle + '\n\n' + errorMessage);
        }

        // Trigger error event
        this.$(document).trigger('form_submit_error', {
            error: error,
            showFieldErrors: showFieldErrors
        });
    }

    /**
     * Get captcha tokens
     * @param $form Optional form element (for inline forms - search within form first)
     */
    private getCaptchaTokens($form?: any): { smartToken: string; recaptchaToken: string } {
        const tokens = {
            smartToken: '',
            recaptchaToken: ''
        };

        // Search in form first (inline form), then in modal
        const $searchScope = ($form && $form.length) ? $form : modal.getModal();

        // Yandex SmartCaptcha
        try {
            if (typeof smartCaptcha !== 'undefined' && smartCaptcha.getResponse) {
                const $captchaContainer = $searchScope.find('.smart-captcha, [data-smart-token]').first();

                if ($captchaContainer.length) {
                    const widgetId = $captchaContainer.attr('data-widget-id');
                    if (widgetId !== undefined && widgetId !== '') {
                        tokens.smartToken = smartCaptcha.getResponse(Number(widgetId)) || '';
                    }

                    // Fallback to data attribute
                    if (!tokens.smartToken) {
                        tokens.smartToken = $captchaContainer.attr('data-smart-token') || '';
                    }
                }
            }
        } catch (e) {
            console.warn('[FormHandler] Error getting SmartCaptcha token:', e);
        }

        // Google reCAPTCHA
        try {
            if (typeof grecaptcha !== 'undefined') {
                const $recaptchaResponse = this.$('#recaptchaResponse');
                if ($recaptchaResponse.length) {
                    tokens.recaptchaToken = $recaptchaResponse.val() || '';
                }

                // Try to get from grecaptcha directly
                if (!tokens.recaptchaToken && grecaptcha.getResponse) {
                    const widgetId = grecaptcha.getResponse();
                    if (widgetId) {
                        tokens.recaptchaToken = widgetId;
                    }
                }
            }
        } catch (e) {
            console.warn('[FormHandler] Error getting reCAPTCHA token:', e);
        }

        return tokens;
    }

    /**
     * Track Yandex Metrika goals
     */
    private trackMetrikaGoals(formId: string): void {
        const metrikaId = dsFormsConfig?.yandexMetrikaId || '';
        if (!metrikaId) {
            return;
        }

        const yaCounter = (window as any)[`yaCounter${metrikaId}`];
        if (typeof yaCounter === 'undefined') {
            return;
        }

        try {
            const $modal = modal.getModal();
            const metrikaGoal = $modal.find('#form2_popup2').attr('data-metrika-goal');

            // Track form-specific goal
            yaCounter.reachGoal(formId);

            // Track additional goal if specified
            if (metrikaGoal) {
                yaCounter.reachGoal(metrikaGoal);
            }

            console.log('[FormHandler] Metrika goals tracked:', formId, metrikaGoal);
        } catch (e) {
            console.warn('[FormHandler] Error tracking Metrika goals:', e);
        }
    }
}

